import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch, connect } from 'react-redux'
import { Field, reduxForm, reset, stopSubmit, formValueSelector } from 'redux-form'
import classNames from 'classnames'
import { InputForm } from 'controls/Input'
import { Row, Col } from 'flexbox'
import { Modal, IonIcon, Button, PasswordStrengthIndicator } from 'controls'
import { store } from 'services'
import { AlertActionCreators } from 'app/reducers/AlertRedux'
import { AuditActionCreators } from 'app/reducers/AuditRedux'
import { profileNewPasswordValidator, mailboxNewPasswordValidator } from 'app/validators/profile'
import { userSelector } from 'app/selectors/user'

import { transformBackendErrors } from 'app/services/passwordErrors'
import styles from './styles'

const PlataformaPasswordForm = ({ handleSubmit, invalid, loading, newPassword }) => (
  <form onSubmit={handleSubmit}>
    <Row>
      <Col xs={12}>
        <Field
          name="newPassword"
          id="auditNewPassword"
          type="password"
          autoComplete="new-password"
          className="form-input-margin"
          component={InputForm}
          placeholder="Nueva contraseña"
          disabled={loading}
        />
        <PasswordStrengthIndicator password={newPassword || ''} show={Boolean(newPassword)} />
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <Field
          name="confirmPassword"
          id="auditConfirmPassword"
          type="password"
          autoComplete="new-password"
          className="form-input-margin"
          component={InputForm}
          placeholder="Confirmar contraseña"
          disabled={loading}
        />
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <Button
          type="submit"
          disabled={invalid || loading}
          color="primary"
          fullWidth
          loading={loading}
        >
          Cambiar contraseña
        </Button>
      </Col>
    </Row>
  </form>
)

const plataformaSelector = formValueSelector('auditPlataformaPassword')

const PlataformaPasswordReduxForm = connect(state => ({
  newPassword: plataformaSelector(state, 'newPassword')
}))(reduxForm({
  form: 'auditPlataformaPassword',
  validate: profileNewPasswordValidator
})(PlataformaPasswordForm))

const MailboxPasswordForm = ({ handleSubmit, invalid, loading, newPassword }) => (
  <form onSubmit={handleSubmit}>
    <Row>
      <Col xs={12}>
        <Field
          name="newPassword"
          id="auditMailboxNewPassword"
          type="password"
          autoComplete="new-password"
          className="form-input-margin"
          component={InputForm}
          placeholder="Nueva contraseña"
          disabled={loading}
        />
        <PasswordStrengthIndicator password={newPassword || ''} show={Boolean(newPassword)} />
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <Field
          name="confirmPassword"
          id="auditMailboxConfirmPassword"
          type="password"
          autoComplete="new-password"
          className="form-input-margin"
          component={InputForm}
          placeholder="Confirmar contraseña"
          disabled={loading}
        />
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <Button
          type="submit"
          disabled={invalid || loading}
          color="primary"
          fullWidth
          loading={loading}
        >
          Cambiar contraseña
        </Button>
      </Col>
    </Row>
  </form>
)

const mailboxSelector = formValueSelector('auditMailboxPassword')

const MailboxPasswordReduxForm = connect(state => ({
  newPassword: mailboxSelector(state, 'newPassword')
}))(reduxForm({
  form: 'auditMailboxPassword',
  validate: mailboxNewPasswordValidator
})(MailboxPasswordForm))

const PasswordAuditModal = () => {
  const dispatch = useDispatch()

  const changeDashboardPass = useSelector(state => state.audit.changeDashboardPass)
  const changeMailboxPass = useSelector(state => state.audit.changeMailboxPass)
  const updateDashboardPassword = useSelector(state => store.updatePassword('user').selector(state))
  const updateMailboxPassword = useSelector(state => store.updateMailboxPassword('user').selector(state))
  const userProfile = useSelector(userSelector)

  const prevDashboardLoading = useRef(false)
  const prevMailboxLoading = useRef(false)

  const showModal = changeDashboardPass || changeMailboxPass
  const showPlataformaForm = changeDashboardPass
  const showMailboxForm = !changeDashboardPass && changeMailboxPass


  // Build user's full email from profile
  const userEmail = userProfile?.mailbox && userProfile?.mailboxDomain
    ? `${userProfile.mailbox}@${userProfile.mailboxDomain}`
    : null
  const userName = userProfile?.firstName || userProfile?.userFullName || ''

  useEffect(() => {
    if (prevDashboardLoading.current && updateDashboardPassword.finished && !updateDashboardPassword.error && showModal) {
      dispatch(AlertActionCreators.success({ message: 'Contraseña de Plataforma iad México actualizada correctamente' }))
      dispatch(AuditActionCreators.clearDashboardPassRequirement())
      dispatch(reset('auditPlataformaPassword'))
      dispatch(AuditActionCreators.refreshPasswordRequirementsRequest())
    } else if (prevDashboardLoading.current && updateDashboardPassword.error) {
      const formErrors = transformBackendErrors(updateDashboardPassword.error)
      if (Object.keys(formErrors).length > 0) {
        dispatch(stopSubmit('auditPlataformaPassword', formErrors))
      }
    }
    prevDashboardLoading.current = updateDashboardPassword.loading
  }, [updateDashboardPassword.loading, updateDashboardPassword.finished, updateDashboardPassword.error, dispatch])

  useEffect(() => {
    if (prevMailboxLoading.current && updateMailboxPassword.finished && !updateMailboxPassword.error) {
      dispatch(AlertActionCreators.success({ message: 'Contraseña del correo actualizada correctamente' }))
      dispatch(AuditActionCreators.clearMailboxPassRequirement())
      dispatch(reset('auditMailboxPassword'))
      dispatch(AuditActionCreators.refreshPasswordRequirementsRequest())
    } else if (prevMailboxLoading.current && updateMailboxPassword.error) {
      const formErrors = transformBackendErrors(updateMailboxPassword.error)
      if (Object.keys(formErrors).length > 0) {
        dispatch(stopSubmit('auditMailboxPassword', formErrors))
      }
    }
    prevMailboxLoading.current = updateMailboxPassword.loading
  }, [updateMailboxPassword.loading, updateMailboxPassword.finished, updateMailboxPassword.error, dispatch])

  const handlePlataformaPasswordSubmit = values => {
    dispatch(store.updatePassword('user').action(values))
  }

  const handleMailboxPasswordSubmit = values => {
    dispatch(store.updateMailboxPassword('user').action(values))
  }

  if (!showModal) {
    return null
  }

  // Build personalized mailbox message
  const getMailboxMessage = () => {
    if (userName && userEmail) {
      return <><strong>{userName}</strong>, por políticas de seguridad debes actualizar la contraseña de tu correo electrónico <strong>{userEmail}</strong></>
    }
    if (userEmail) {
      return <>Por políticas de seguridad, debes actualizar la contraseña de tu correo electrónico <strong>{userEmail}</strong></>
    }
    return <>Por políticas de seguridad, debes actualizar la contraseña de tu correo electrónico.</>
  }

  return (
    <Modal isOpen={showModal} showCloseButton={false}>
      <section className="content">
        <div className={`password-type-badge ${showPlataformaForm ? 'plataforma' : 'mailbox'}`}>
          <IonIcon icon={showPlataformaForm ? 'ios-apps' : 'ios-mail'} />
          {showPlataformaForm ? 'PLATAFORMA iad México' : 'CORREO'}
        </div>
        <h3 className='title'>
          {showPlataformaForm
            ? 'Cambiar contraseña de Plataforma iad México'
            : 'Cambiar contraseña de tu Correo'}
        </h3>
        <p className={classNames("subtitle", { 'platform-message': showPlataformaForm })}>
          {showPlataformaForm
            ? 'Por políticas de seguridad, debes actualizar la contraseña que usas para acceder a la Plataforma iad México.'
            : getMailboxMessage()}
        </p>
        {!showPlataformaForm && (
          <p className='mailbox-warning'>
            Recuerda que debes añadir tu nuevo pass a cualquier dispositivo que hayas conectado y si lo tienes integrado al CRM también debes de actualizarlo ahí.
          </p>
        )}
        <div className="form-container">
          {showPlataformaForm && (
            <PlataformaPasswordReduxForm
              onSubmit={handlePlataformaPasswordSubmit}
              loading={updateDashboardPassword.loading}
            />
          )}
          {showMailboxForm && (
            <MailboxPasswordReduxForm
              onSubmit={handleMailboxPasswordSubmit}
              loading={updateMailboxPassword.loading}
              userProfile={userProfile}
            />
          )}
        </div>
      </section>
      <style jsx>{styles}</style>
    </Modal>
  )
}

export default PasswordAuditModal
